import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cities as defaultCities, getCityBySlug } from '../config/cities';
import { applyTheme } from '../utils/theme';
import { loadCityConfig, saveCityConfig } from '../utils/storage';

const CityContext = createContext(null);

export function CityProvider({ children }) {
  const [activeCity, setActiveCity] = useState(null);
  // Merge persisted custom configs over the defaults
  const [cityList, setCityList] = useState(() => {
    const saved = loadCityConfig();
    if (!saved) return defaultCities;
    return defaultCities.map((c) => {
      const override = saved.find((s) => s.slug === c.slug);
      return override ? { ...c, ...override, colors: { ...c.colors, ...override.colors } } : c;
    });
  });

  const activateCity = useCallback(
    (slug) => {
      const city = cityList.find((c) => c.slug === slug) || getCityBySlug(slug);
      setActiveCity(city || null);
      applyTheme(city || null);
    },
    [cityList]
  );

  const deactivateCity = useCallback(() => {
    setActiveCity(null);
    applyTheme(null);
  }, []);

  const updateCityConfig = useCallback(
    (slug, patch) => {
      setCityList((prev) => {
        const next = prev.map((c) =>
          c.slug === slug
            ? { ...c, ...patch, colors: { ...c.colors, ...(patch.colors || {}) } }
            : c
        );
        saveCityConfig(next);
        // Re-apply theme if this is the active city
        if (activeCity && activeCity.slug === slug) {
          const updated = next.find((c) => c.slug === slug);
          applyTheme(updated);
          setActiveCity(updated);
        }
        return next;
      });
    },
    [activeCity]
  );

  const addCity = useCallback((newCity) => {
    setCityList((prev) => {
      const next = [...prev, newCity];
      saveCityConfig(next);
      return next;
    });
  }, []);

  // Keep theme in sync when city list changes externally
  useEffect(() => {
    if (activeCity) {
      const refreshed = cityList.find((c) => c.slug === activeCity.slug);
      if (refreshed) applyTheme(refreshed);
    }
  }, [cityList, activeCity]);

  return (
    <CityContext.Provider
      value={{ activeCity, cityList, activateCity, deactivateCity, updateCityConfig, addCity }}
    >
      {children}
    </CityContext.Provider>
  );
}

export function useCityContext() {
  const ctx = useContext(CityContext);
  if (!ctx) throw new Error('useCityContext must be used inside CityProvider');
  return ctx;
}
